/**
 * API client per la review workspace dei casi demo reali (Sprint 5).
 *
 * Rispecchia i read-model di `backend/app/schemas/demo.py` e le route di
 * `backend/app/api/demo.py`. I nomi dei campi non sono inventati: ogni tipo
 * qui sotto corrisponde alla risposta reale del backend.
 *
 * Endpoints (tutti dietro autenticazione, prefisso condiviso /api/v1):
 * - GET  /demo/runs                          - Lista run
 * - GET  /demo/runs/:runId                   - Dettaglio run con casi
 * - GET  /demo/case-results/:resultId        - Dettaglio tecnico/clinico caso
 * - PUT  /demo/case-results/:resultId/review - Salva review (optimistic concurrency)
 * - GET  /demo/runs/:runId/export            - Export JSON redatto
 */

import axios from 'axios';
import api from './api';

export type ReviewStatus = 'approved' | 'corrected';
export type FieldDiffStatus = 'match' | 'mismatch' | 'not_scorable';
export type AnnotationStatus = 'provisional' | 'verified' | 'validated';
export type SenderSegment =
  | 'owner'
  | 'referring_vet'
  | 'specialist'
  | 'administrative';
export type Priority = 'low' | 'medium' | 'high' | 'urgent';
export type CaseIntent =
  | 'second_opinion'
  | 'surgery_candidacy'
  | 'quote_availability'
  | 'appointment_followup'
  | 'urgent_consult'
  | 'general_information'
  | 'therapy_question'
  | 'administrative';

/**
 * Chiavi ammesse in `correction_data`: sono esattamente le chiavi della
 * whitelist applicata dal backend (`CORRECTION_DATA_KEYS`). Il tipo e' chiuso
 * di proposito: una chiave fuori elenco non deve compilare.
 */
export interface DemoCorrectionData {
  sender_segment?: SenderSegment;
  intents?: CaseIntent[];
  priority?: Priority;
  specialty?: string;
  clinical_fields?: Record<string, unknown>;
  missing_information?: string[];
  follow_up_questions?: string[];
  suspected_neoplasia?: boolean;
  out_of_domain?: boolean;
  requires_human_review?: boolean;
}

export interface DemoReviewRequest {
  status: ReviewStatus;
  correction_data: DemoCorrectionData;
  notes?: string | null;
  /** Revisione attesa: 0 = insert, >0 = update condizionato. */
  expected_revision: number;
}

export interface DemoReviewResponse {
  id: string;
  case_result_id: string;
  reviewer_id: string | null;
  status: ReviewStatus;
  correction_data: DemoCorrectionData;
  notes: string | null;
  revision: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface DemoFailure {
  code: string | null;
  summary: string | null;
}

export interface DemoStage {
  stage: string;
  status: string;
  failure_code: string | null;
  error_summary: string | null;
}

export interface DemoCoverage {
  messages: number;
  nested_messages: number;
  attachments: number;
  expected_nested_messages: number | null;
  expected_attachments: number | null;
}

export interface DemoPrivacyCounts {
  findings: number;
  occurrences: number;
}

export interface DemoTechnicalDetail {
  status: string;
  failure: DemoFailure;
  current_stage: string | null;
  stages: DemoStage[];
  source_hash: string | null;
  coverage: DemoCoverage;
  privacy: DemoPrivacyCounts;
}

export interface FieldReview {
  status: AnnotationStatus;
  reviewer_id: string | null;
  reviewed_at: string | null;
}

export interface DemoFieldDiff {
  field: string;
  expected: unknown;
  proposed: unknown;
  corrected: unknown;
  status: FieldDiffStatus;
  annotation_status: AnnotationStatus | null;
}

export interface DemoClinicalDiff {
  fields: DemoFieldDiff[];
  match_count: number;
  mismatch_count: number;
  not_scorable_count: number;
}

export interface DemoClinicalDetail {
  analysis: Record<string, unknown> | null;
  expected: Record<string, unknown> | null;
  diff: DemoClinicalDiff;
  overall_annotation_status: AnnotationStatus | null;
  field_reviews: Record<string, FieldReview>;
}

export interface DemoImageFinding {
  logical_path: string;
  parent_message_id: string | null;
  filename: string | null;
  sha256: string;
  status: string;
  /**
   * Pagina 1-based per i reperti tratti da una pagina di PDF; assente per i
   * JPEG (il backend la omette dal JSON quando e' `None`).
   */
  page?: number | null;
  doc_type: string | null;
  region: string | null;
  image_quality: string | null;
  findings: string[];
  impression: string | null;
  limits: string[];
  form_fields: Record<string, string>;
  confidence: number | null;
  reason: string | null;
  model: string | null;
  prompt_version: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
}

export interface DemoDocumentFinding {
  logical_path: string;
  parent_message_id: string;
  filename: string | null;
  sha256: string;
  status: string;
  doc_type: string | null;
  title: string | null;
  text_excerpt: string | null;
  pages_total: number;
  pages_read: number;
  source: string | null;
  limits: string[];
  reason: string | null;
  model: string | null;
  prompt_version: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
}

export interface DemoCaseDetail {
  id: string;
  run_id: string;
  case_key: string;
  technical: DemoTechnicalDetail;
  clinical: DemoClinicalDetail;
  images: DemoImageFinding[];
  documents: DemoDocumentFinding[];
  review: DemoReviewResponse | null;
}

export interface DemoCaseListItem {
  id: string;
  case_key: string;
  status: string;
  failure_code: string | null;
  current_stage: string | null;
  review_status: ReviewStatus | null;
  review_revision: number | null;
}

export interface DemoRunListItem {
  id: string;
  dataset_name: string;
  dataset_version: string;
  dataset_kind: string;
  status: string;
  git_commit: string | null;
  prompt_version: string | null;
  seed: number | null;
  started_at: string | null;
  completed_at: string | null;
  summary: Record<string, unknown>;
}

export interface DemoRunDetail extends DemoRunListItem {
  cases: DemoCaseListItem[];
}

/** Caso dentro l'export JSON redatto (`/demo/runs/:id/export`). */
export interface DemoRunExportCase {
  id: string;
  run_id: string;
  case_key: string;
  status: string;
  failure: DemoFailure;
  current_stage: string | null;
  metrics: Record<string, unknown>;
  technical: DemoTechnicalDetail;
  clinical: DemoClinicalDetail;
  images: DemoImageFinding[];
  documents: DemoDocumentFinding[];
  review: DemoReviewResponse | null;
}

export interface DemoRunExport {
  run: DemoRunDetail;
  cases: DemoRunExportCase[];
  exported_at: string;
}

/**
 * Errore tipizzato del dominio demo.
 *
 * Il backend risponde agli errori con `{detail: {code, message}}`: esponiamo
 * `code` per distinguere un conflitto di revisione da un permesso negato senza
 * confrontare stringhe.
 */
export class DemoApiError extends Error {
  readonly code: string;
  readonly status: number | null;

  constructor(code: string, message: string, status: number | null = null) {
    super(message);
    this.name = 'DemoApiError';
    this.code = code;
    this.status = status;
  }
}

function toDemoApiError(error: unknown): DemoApiError {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (
      detail !== null &&
      typeof detail === 'object' &&
      'code' in detail &&
      typeof (detail as { code?: unknown }).code === 'string'
    ) {
      const { code, message } = detail as { code: string; message?: unknown };
      return new DemoApiError(
        code,
        typeof message === 'string' ? message : error.message,
        error.response?.status ?? null,
      );
    }
    return new DemoApiError(
      'unknown_error',
      error.message,
      error.response?.status ?? null,
    );
  }
  if (error instanceof Error) {
    return new DemoApiError('unknown_error', error.message);
  }
  return new DemoApiError('unknown_error', String(error));
}

async function request<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    throw toDemoApiError(error);
  }
}

export const demoApi = {
  listRuns: async (): Promise<DemoRunListItem[]> =>
    request(async () => {
      const response = await api.get<DemoRunListItem[]>('/demo/runs');
      return response.data;
    }),

  getRun: async (id: string): Promise<DemoRunDetail> =>
    request(async () => {
      const response = await api.get<DemoRunDetail>(`/demo/runs/${id}`);
      return response.data;
    }),

  getCaseResult: async (id: string): Promise<DemoCaseDetail> =>
    request(async () => {
      const response = await api.get<DemoCaseDetail>(`/demo/case-results/${id}`);
      return response.data;
    }),

  reviewCase: async (
    id: string,
    payload: DemoReviewRequest,
  ): Promise<DemoReviewResponse> =>
    request(async () => {
      const response = await api.put<DemoReviewResponse>(
        `/demo/case-results/${id}/review`,
        payload,
      );
      return response.data;
    }),

  exportRun: async (id: string): Promise<DemoRunExport> =>
    request(async () => {
      const response = await api.get<DemoRunExport>(`/demo/runs/${id}/export`);
      return response.data;
    }),

  getCaseMessages: async (id: string): Promise<DemoMessageItem[]> =>
    request(async () => {
      const response = await api.get<DemoMessageItem[]>(
        `/demo/case-results/${id}/messages`,
      );
      return response.data;
    }),

  getCaseAttachment: async (
    id: string,
    sha256: string,
    page?: number,
  ): Promise<Blob> =>
    request(async () => {
      const response = await api.get<Blob>(
        `/demo/case-results/${id}/attachments/${sha256}`,
        page === undefined
          ? { responseType: 'blob' }
          : { responseType: 'blob', params: { page } },
      );
      return response.data;
    }),
};

export interface DemoMessageItem {
  message_id: string | null;
  logical_path: string | null;
  depth: number;
  subject: string | null;
  from_address: string | null;
  to_addresses: string[];
  received_at: string | null;
  body_text: string | null;
  body_html: string | null;
  attachments: Record<string, unknown>[];
}
